import { type GAMAdvert, type StrevdaContext } from '@skyscanner/strevda-gam';
import { ERROR_BOUNDARY_PREFIX_QUARK } from '@web-platform/shared-universal-constants/src/adverts';
import { STREVDA_SLOTS } from '@web-platform/shared-universal-types/src/network/requestTypes';
import { importRetryWrapper } from '@web-platform/shared-universal-utils-frontend/src/helpers/lazyLoading';
import { logWarn } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

import { formatStrevdaVariables } from './formatStrevdaVariables';

import type { Slot } from '@web-platform/shared-universal-types/src/network/endpoints/strevda';
import type { QueryItem } from '@web-platform/shared-universal-types/src/redux/state/network/queries';

const loadStrevdaGam = (item: QueryItem): Promise<Slot[]> =>
  new Promise((resolve, reject) => {
    importRetryWrapper(
      () =>
        import(
          /* webpackChunkName: "StrevdaGam" */
          '@skyscanner/strevda-gam'
        ),
    )
      .then(async (strevdaModule) => {
        const { DEFAULT_SLOT_REQUEST_TIMEOUT, compatibilityUtils, gamService } = strevdaModule;
        // init strevda
        // https://github.skyscannertools.net/adverts/strevda-gam/tree/master#through-bootstrap
        try {
          const { device, pageName } = item.variables;
          const platform = device.isMobilePhone ? 'mobile' : 'desktop';
          const updatedVariables = formatStrevdaVariables(item.variables);

          const slotRequestData = compatibilityUtils.buildSlotSetup(
            'inline-slot-1',
            `${platform}.${pageName}/inline`,
            'flightsInline',
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            updatedVariables as StrevdaContext,
          );
          const slotData: GAMAdvert[] = await gamService.requestAdsFromGAM(
            [slotRequestData],
            { slotRequestTimeout: DEFAULT_SLOT_REQUEST_TIMEOUT },
            { enabled: false },
            false,
          );

          resolve(
            slotData.map((advert) => ({
              position: 1,
              advert,
            })),
          );
        } catch (error) {
          logWarn({
            error,
            component: `${ERROR_BOUNDARY_PREFIX_QUARK}StrevdaSlotsClient`,
            message: 'Error initialising strevda, GAM ads will not show',
          });
          reject(error);
        }
      })
      .catch((error) => {
        logWarn({
          error,
          component: `${ERROR_BOUNDARY_PREFIX_QUARK}StrevdaSlotsClient`,
          message: 'Failed to load the strevda chunk, GAM ads will not show',
        });
        reject(error);
      });
  });

const shimIntoNetworkResponse = (slots: Slot[]) => ({
  error: false,
  headers: {},
  body: slots,
  status: 200,
  type: STREVDA_SLOTS,
});

export const strevdaSlotsRequest = (item: QueryItem) =>
  loadStrevdaGam(item).then(shimIntoNetworkResponse);
